/**
 * 🏁 Final Rounds — the fourth board tab.
 *
 * A final round is a CHAIN of back-to-back interviews for one candidate (final
 * interview + bar raiser + optional get-to-knows), solved server-side with
 * segment ORDERINGS, not just start times. This tab shows, per role: the round
 * template, the solved 2–3 chain options, and the candidates waiting — with
 * the proposed → confirmed → booked → debrief-done tracking flow.
 */
import { useState } from 'react';
import { Accordion } from '@finn-auto/design-system/atoms/accordion';
import { Button } from '@finn-auto/design-system/atoms/button';
import type {
  ChainOption,
  RoleBoard,
  SchedulingCandidate,
  SlotHold,
  TrackingMap,
} from '../../types/taActionBoard';
import type { FinalRoundSuggestion } from '../../services/taActionBoardApi';
import { ChainRow } from './ChainRow';
import { FinalRoundCard } from './FinalRoundCard';
import { HoldOverview } from './HoldOverview';
import {
  COLORS,
  firstNameFromEmail,
  fullNameFromEmail,
  roundLengthLabel,
  sortChainsForDisplay,
} from './format';
import { RoleAccordion, SummaryChip } from './RoleAccordion';
import { RolePipeline } from './RolePipeline';
import { useFocusCard, type FocusTarget } from './useFocusCard';
import { NoRoundTemplateState } from './RoundTemplateSuggestion';
import { useBoardAvailability } from './useBoardAvailability';
import { AvailabilityNote, AvailabilityPill } from '../../components/interviewAvailability/AvailabilityLight';

export type FinalRoundAction =
  /** suggestion: pre-fill parsed from the kick-off sheet (never auto-saved) */
  | { type: 'fr-config'; suggestion?: FinalRoundSuggestion | null }
  /** Re-solve THIS role's chains against fresh calendars (the 🔄 button) */
  | { type: 'fr-recheck' }
  /** Check the ticked options with the hiring team BEFORE offering them. */
  | { type: 'fr-ask'; options: ChainOption[] }
  /** preselected: ISO starts of the chain options ticked in the card above */
  | { type: 'fr-propose'; candidate: SchedulingCandidate; preselected?: string[] }
  | { type: 'fr-book'; candidate: SchedulingCandidate }
  /** Take back times already given to the candidate — see FinalRoundModals. */
  | { type: 'fr-withdraw'; candidate: SchedulingCandidate }
  | { type: 'fr-debrief'; candidate: SchedulingCandidate };

interface FinalRoundsTabProps {
  roles: RoleBoard[];
  /** Whose board is on screen — the shared pipeline strip's Lever retry needs it. */
  boardEmail: string;
  /** Reload the board after that retry. */
  onChanged?: () => void;
  /** The signed-in user — their Google grant reads the kick-off sheet. */
  myEmail: string;
  /** opportunityId → "Debrief done" … — cards render muted. */
  handled: Record<string, string>;
  /** Candidate the Overdue tab sent us to — expand their role and scroll to them. */
  focus?: FocusTarget | null;
  tracking: TrackingMap;
  /**
   * The answering proxy revision predates the finalRounds section (see
   * utils/taBoardPayload.ts). There is genuinely nothing to show, so say that
   * rather than render an empty list that reads as "no work to do".
   */
  unavailable?: boolean;
  onAction: (role: RoleBoard, action: FinalRoundAction) => void;
  /** postingIds whose 🔄 re-check is in flight */
  rechecking?: Set<string>;
  /** postingId → the role's final-round holds (kept out of the cached board). */
  finalHolds?: Record<string, SlotHold[]>;
  /** Block the ticked chains — one tentative hold per chain window. */
  onHoldChains?: (postingId: string, starts: string[]) => void;
  onReleaseFinalHold?: (postingId: string, hold: SlotHold) => void;
}

export function FinalRoundsTab({
  roles,
  boardEmail,
  onChanged,
  handled,
  focus = null,
  tracking,
  myEmail,
  unavailable,
  onAction,
  rechecking,
  finalHolds,
  onHoldChains,
  onReleaseFinalHold,
}: FinalRoundsTabProps) {
  // Hooks must run on every render, so the accordion state is initialized
  // BEFORE the `unavailable` branch below (react-hooks/rules-of-hooks).
  const [open, setOpen] = useState<string[]>(() => {
    const first = roles.find((r) => r.finalRounds.candidates.length > 0)?.postingId;
    return first ? [first] : [];
  });
  useFocusCard(focus, setOpen);
  const { data: availability } = useBoardAvailability(roles);

  if (unavailable) return <UnavailableNotice />;

  // Internships never get a final round — the server marks them notApplicable
  // and the tab pretends they do not exist.
  const shown = roles.filter((r) => !r.finalRounds.notApplicable);
  const active = shown.filter((r) => r.finalRounds.candidates.length > 0);
  const waiting = active.reduce((n, r) => n + r.finalRounds.candidates.length, 0);
  const freeChains = active.reduce(
    (n, r) => n + r.finalRounds.chains.filter((c) => c.grade === 'free').length,
    0
  );
  const noTemplate = active.filter((r) => !r.finalRounds.config).length;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        <SummaryChip label={`${waiting} candidates waiting`} bg={COLORS.cotton} text="#484848" />
        <SummaryChip label={`${freeChains} final rounds ready to book`} bg="#DCF5E4" text="#136C34" />
        <SummaryChip
          label={`${noTemplate} roles without a round template`}
          bg={COLORS.orangeBg}
          text={COLORS.orangeText}
        />
      </div>

      <Accordion type="multiple" value={open} onValueChange={setOpen} className="space-y-3">
        {shown.map((role) => {
          const n = role.finalRounds.candidates.length;
          const booked = role.finalRounds.scheduled?.length ?? 0;
          return (
            <RoleAccordion
              key={role.postingId}
              role={role}
              // A role with nothing left to book must still open: the rounds it HAS booked are
              // in the collapsed list inside.
              hasOpenItems={n > 0 || booked > 0}
              chip={{ label: `${n} final round${n === 1 ? '' : 's'}`, bg: COLORS.blueBg, text: COLORS.blueText }}
              aside={
                availability[role.postingId] ? (
                  <AvailabilityPill
                    stage={availability[role.postingId]?.final}
                    round="Final"
                    windowEnd={availability[role.postingId]?.windowEnd ?? ''}
                  />
                ) : null
              }
            >
              <AvailabilityNote
                stage={availability[role.postingId]?.final}
                round="Final"
                windowEnd={availability[role.postingId]?.windowEnd ?? ''}
              />
              <RoleFinalRounds
                role={role}
                handled={handled}
                highlightId={focus?.opportunityId ?? null}
                tracking={tracking}
                myEmail={myEmail}
                boardEmail={boardEmail}
                onChanged={onChanged}
                onAction={onAction}
                rechecking={rechecking}
                holds={finalHolds?.[role.postingId] ?? []}
                onHoldChains={onHoldChains}
                onReleaseFinalHold={onReleaseFinalHold}
              />
            </RoleAccordion>
          );
        })}
      </Accordion>
    </div>
  );
}

/**
 * The answering proxy predates the finalRounds section. An empty role list
 * would read as "no work to do", so name the actual reason instead.
 */
function UnavailableNotice() {
  return (
    <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-gray-300 bg-gray-50 px-4 py-10 text-center">
      <p className="text-sm font-semibold text-gray-700">Final rounds aren't available yet</p>
      <p className="max-w-lg text-xs text-gray-500">
        This page needs an update to the board service. Every other tab works as usual; hit Refresh
        once it's updated.
      </p>
    </div>
  );
}

function RoleFinalRounds({
  role,
  handled,
  highlightId = null,
  tracking,
  myEmail,
  boardEmail,
  onChanged,
  onAction,
  rechecking,
  holds,
  onHoldChains,
  onReleaseFinalHold,
}: {
  role: RoleBoard;
  boardEmail: string;
  onChanged?: () => void;
  handled: Record<string, string>;
  /** Candidate arrived at from the Overdue tab — ringed so the landing is visible. */
  highlightId?: string | null;
  tracking: TrackingMap;
  myEmail: string;
  onAction: (role: RoleBoard, action: FinalRoundAction) => void;
  rechecking?: Set<string>;
  holds: SlotHold[];
  onHoldChains?: (postingId: string, starts: string[]) => void;
  onReleaseFinalHold?: (postingId: string, hold: SlotHold) => void;
}) {
  const fr = role.finalRounds;
  // Which chain options this role will offer. Lives here, next to the card and
  // the candidate list, exactly like the case panel's ticked slots: the options
  // belong to the ROLE, the proposal belongs to a candidate.
  const [selected, setSelected] = useState<string[]>([]);
  if (fr.candidates.length === 0) {
    return (
      <div className="space-y-2">
        <p className="text-sm text-gray-300">Nothing open — no candidate waits for a final round.</p>
        {/* …which is only half the story on a role whose final rounds were all booked this
            morning. Without this the sentence above reads as "nobody is being interviewed". */}
        <RolePipeline role={role} boardEmail={boardEmail} onChanged={onChanged} />
      </div>
    );
  }
  return (
    <div className="space-y-3">
      {fr.config ? (
        <ChainOptionsCard
          role={role}
          selected={selected}
          onToggle={(start) =>
            setSelected((prev) =>
              prev.includes(start) ? prev.filter((s) => s !== start) : [...prev, start]
            )
          }
          onHold={onHoldChains ? () => onHoldChains(role.postingId, selected) : undefined}
          onAskPanel={() =>
            onAction(role, {
              type: 'fr-ask',
              options: [...fr.chains, ...(fr.moreChains || [])].filter((c) => selected.includes(c.start)),
            })
          }
          onEditTemplate={() => onAction(role, { type: 'fr-config' })}
          onRecheck={() => onAction(role, { type: 'fr-recheck' })}
          rechecking={rechecking?.has(role.postingId) ?? false}
        />
      ) : (
        <NoRoundTemplateState
          postingId={role.postingId}
          myEmail={myEmail}
          onConfigure={(suggestion) => onAction(role, { type: 'fr-config', suggestion })}
        />
      )}
      {holds.length > 0 && (
        <HoldOverview
          holds={holds}
          legacyCount={0}
          onRelease={(hold) => {
            if (onReleaseFinalHold && window.confirm('Release this final-round hold from the panel’s calendars?')) {
              onReleaseFinalHold(role.postingId, hold);
            }
          }}
          onReleaseLegacy={() => undefined}
        />
      )}
      <div className="space-y-3 rounded-lg p-3" style={{ backgroundColor: COLORS.cotton }}>
        {fr.candidates.map((candidate) => (
          <FinalRoundCard
            key={candidate.opportunityId}
            candidate={candidate}
            highlighted={highlightId === candidate.opportunityId}
            handledLabel={handled[candidate.opportunityId] || null}
            tracking={tracking}
            hasChains={fr.chains.length > 0}
            onAction={(action) =>
              onAction(
                role,
                // The ticked options ride along, so the proposal modal opens on
                // what the recruiter just chose rather than a default guess.
                action.type === 'fr-propose' ? { ...action, preselected: selected } : action
              )
            }
          />
        ))}
      </div>
      {/* LAST, and collapsed: where the ones already booked went. Not work, so not above it. */}
      <RolePipeline role={role} boardEmail={boardEmail} onChanged={onChanged} />
    </div>
  );
}

/** Template line + the solved chain options with their segment timelines. */
function ChainOptionsCard({
  role,
  selected,
  onToggle,
  onHold,
  onAskPanel,
  onEditTemplate,
  onRecheck,
  rechecking,
}: {
  role: RoleBoard;
  /** ISO starts of the ticked options — what a proposal will offer. */
  selected: string[];
  onToggle: (start: string) => void;
  /** Block the ticked chains on the panel's calendars. Absent = holds off. */
  onHold?: () => void;
  onAskPanel: () => void;
  onEditTemplate: () => void;
  onRecheck: () => void;
  rechecking: boolean;
}) {
  const { config, chains, moreChains, chainError, chainDiagnosis, lookaheadDays } = role.finalRounds;
  const [showMore, setShowMore] = useState(false);
  const extra = moreChains || [];
  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-xs text-gray-600">
          <span className="font-semibold text-gray-800">Round:</span>{' '}
          {fullNameFromEmail(config!.finalInterviewer)} (final) + {fullNameFromEmail(config!.barRaiser)} (bar raiser)
          {config!.g2k.length > 0 ? ` · Get-to-know (${config!.g2kMinutes} min): ${config!.g2k.map(firstNameFromEmail).join(', ')}` : ''}
          {config!.recruiterCheckout ? ` · Recruiter check-out (15 min)${config!.recruiterCheckoutEmail ? `: ${firstNameFromEmail(config!.recruiterCheckoutEmail)}` : ''}` : ''}
          {` · ${roundLengthLabel(config!)}`}
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          {/* Same pair as the case panel: re-read these calendars without a
              full board pull, then edit the template. */}
          <Button
            size="sm"
            variant="ghost"
            onClick={onRecheck}
            disabled={rechecking}
            title="Refresh this round's times against fresh calendars — faster than a full board refresh"
          >
            {rechecking ? 'Refreshing…' : 'Refresh'}
          </Button>
          {/* Hold the ticked chains, exactly like the case panel's "Hold
              selected slots": tentative blocks so the time is not lost while the
              candidate decides. Released automatically once the round is booked. */}
          {onHold && (
            <Button
              size="sm"
              variant="ghost"
              disabled={selected.length === 0}
              onClick={onHold}
              title={
                selected.length === 0
                  ? 'Tick the times you want to hold'
                  : 'Block these times on the panel’s calendars as tentative holds'
              }
            >
              Hold selected{selected.length > 0 ? ` (${selected.length})` : ''}
            </Button>
          )}
          {/* Before the candidate hears anything: does the panel agree? Lives
              here, with the options, rather than behind the booking button. */}
          <Button
            size="sm"
            variant="ghost"
            disabled={selected.length === 0}
            onClick={onAskPanel}
            title={
              selected.length === 0
                ? 'Tick the options you want the panel to look at'
                : 'Ask the hiring team whether these work — nothing reaches the candidate'
            }
          >
            Ask panel{selected.length > 0 ? ` (${selected.length})` : ''}
          </Button>
          <Button size="sm" variant="ghost" onClick={onEditTemplate}>
            Edit round template
          </Button>
        </div>
      </div>
      {chainError ? (
        <p className="mt-2 text-xs text-gray-500">No times available: {chainError}</p>
      ) : chains.length === 0 ? (
        <p className="mt-2 text-xs text-gray-500">
          No workable final round in the next {lookaheadDays ?? 10} days
          {chainDiagnosis ? (
            // The specific reason beats the generic advice — "adjust the
            // template" is no help when a participant is simply away.
            <> — {chainDiagnosis}</>
          ) : (
            <> — adjust the template or check calendars.</>
          )}
        </p>
      ) : (
        <>
          <ul className="mt-2 space-y-1.5">
            {sortChainsForDisplay(chains).map((chain, i) => (
              <ChainRow
                key={chain.start}
                chain={chain}
                index={i}
                checked={selected.includes(chain.start)}
                onToggle={() => onToggle(chain.start)}
              />
            ))}
            {showMore &&
              extra.map((chain, j) => (
                <ChainRow
                  key={chain.start}
                  chain={chain}
                  index={chains.length + j}
                  checked={selected.includes(chain.start)}
                  onToggle={() => onToggle(chain.start)}
                />
              ))}
          </ul>
          {extra.length > 0 && (
            <Button variant="ghost" size="sm" className="mt-1 h-auto px-0 text-[11px]" onClick={() => setShowMore((v) => !v)}>
              {showMore ? 'Show fewer' : `Show ${extra.length} more time${extra.length === 1 ? '' : 's'}`}
            </Button>
          )}
          <p className="mt-2 text-[11px] text-gray-500">
            {selected.length === 0
              ? 'Tick the times you want to propose, then send them on a candidate below.'
              : `${selected.length} option${selected.length === 1 ? '' : 's'} ticked — send ${
                  selected.length === 1 ? 'it' : 'them'
                } with “Propose times” on a candidate below.`}
          </p>
        </>
      )}
    </div>
  );
}
